import React from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="w-full bg-gray-800/50 border-t border-gray-700/50 backdrop-blur-sm mt-auto">
      <div className="w-full px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="flex flex-col space-y-8 sm:space-y-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Column 1: About */}
            <div className="space-y-4">
              <h3 className="text-gray-300 font-medium">About</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Your Open Org Content Hub is a curated collection of people and culture resources and exclusive partner discounts.
              </p>
              <a
                href="https://news.openorg.fyi/subscribe"
                target="_blank"
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                <span>Subscribe to our newsletter</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
            </div>

            {/* Column 2: Quick Links */}
            <div className="space-y-4">
              <h3 className="text-gray-300 font-medium">Quick Links</h3>
              <div className="grid grid-cols-2 gap-2">
                <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Terms
                </Link>
                <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Privacy
                </Link>
                <a
                  href="https://www.linkedin.com/school/the-open-org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  LinkedIn
                </a>
              </div>
            </div>

            {/* Column 3: Open Org Products */}
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <h3 className="text-gray-300 font-medium">Open Org Ecosystem</h3>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <a 
                  href="https://www.openorg.fyi/culture-support?utm_source=membership.openorg.fyi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-300"
                >
                  {/* Open Org Logo */}
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="5"
                      className="stroke-gray-400 group-hover:stroke-gray-200 transition-colors duration-300"
                      strokeWidth="2"
                      transform="translate(-3, 0)"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="5"
                      className="stroke-gray-400 group-hover:stroke-gray-200 transition-colors duration-300"
                      strokeWidth="2"
                      transform="translate(3, 0)"
                    />
                  </svg>
                  <span className="text-sm text-gray-400 group-hover:text-gray-200">
                    Culture Support
                  </span>
                </a>
                <a 
                  href="https://www.openverse.fyi?utm_source=membership.openorg.fyi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-300"
                >
                  {/* Saturn/Planet with Ring Icon */}
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <ellipse cx="12" cy="12" rx="10" ry="3" />
                  </svg>
                  <span className="text-sm text-gray-400 group-hover:text-gray-200">
                    Openverse
                  </span>
                </a>
                <a 
                  href="https://peoplemetrics.fyi/?utm_source=membership.openorg.fyi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-300"
                >
                  {/* Chart Icon for Peoplemetrics */}
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <path d="M18 17V9" />
                    <path d="M13 17V5" />
                    <path d="M8 17v-3" />
                  </svg>
                  <span className="text-sm text-gray-400 group-hover:text-gray-200">
                    Peoplemetrics.fyi
                  </span>
                </a>
                <a 
                  href="https://www.openorg.fyi/community?utm_source=membership.openorg.fyi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-300"
                >
                  {/* Slack Icon */}
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-200" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.194 14.644c0 1.16-.943 2.107-2.103 2.107-1.16 0-2.103-.947-2.103-2.107 0-1.16.943-2.106 2.103-2.106h2.103v2.106zm1.061 0c0-1.16.944-2.106 2.104-2.106 1.16 0 2.103.946 2.103 2.106v5.274c0 1.16-.943 2.107-2.103 2.107-1.16 0-2.104-.947-2.104-2.107v-5.274z" />
                    <path d="M9.359 6.106c-1.16 0-2.104-.946-2.104-2.106 0-1.16.944-2.106 2.104-2.106 1.16 0 2.103.946 2.103 2.106v2.106H9.36zm0 1.06c1.16 0 2.103.947 2.103 2.107 0 1.16-.943 2.107-2.103 2.107H4.092c-1.16 0-2.104-.947-2.104-2.107 0-1.16.944-2.107 2.104-2.107h5.267z" />
                    <path d="M17.788 9.273c0-1.16.943-2.107 2.103-2.107 1.16 0 2.103.947 2.103 2.107 0 1.16-.943 2.107-2.103 2.107h-2.103V9.273zm-1.06 0c0 1.16-.944 2.107-2.104 2.107-1.16 0-2.103-.947-2.103-2.107V4c0-1.16.943-2.106 2.103-2.106 1.16 0 2.104.946 2.104 2.106v5.273z" />
                    <path d="M14.624 17.733c1.16 0 2.104.947 2.104 2.107 0 1.16-.944 2.106-2.104 2.106-1.16 0-2.103-.946-2.103-2.106v-2.107h2.103zm0-1.06c-1.16 0-2.103-.947-2.103-2.107 0-1.16.943-2.107 2.103-2.107h5.267c1.16 0 2.104.947 2.104 2.107 0 1.16-.944 2.107-2.104 2.107h-5.267z" />
                  </svg>
                  <span className="text-sm text-gray-400 group-hover:text-gray-200">
                    Community
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />

          {/* Bottom Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>©</span>
              <span>2025</span>
              <span>Open Org Group Ltd</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-500">
                Built with ♥️ by{' '}
                <a 
                  href="https://www.linkedin.com/in/jfw/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  John
                </a>
                {' '}&{' '}
                <a 
                  href="https://www.linkedin.com/in/adam-open-org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Adam
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 